import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  User,
  Award,
  Send,
  MapPin,
  Wrench,
  BookOpen,
  Sliders,
  CreditCard,
  FileText,
  Database,
  Download,
  Trash2,
  Shield,
  ChevronRight,
  Info,
  UserCheck,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import DeletionCountdownNotice from "../components/DeletionCountdownNotice";

type SummaryGroup = {
  key: string;
  title: string;
  count: number;
  details: string[];
};

type AccountSummary = {
  account: {
    email: string;
    name: string | null;
    isAdmin: boolean;
    registeredAt: string | null;
  };
  groups: SummaryGroup[];
};

const GROUP_ICONS: Record<string, React.ReactNode> = {
  konto: <User size={18} className="text-brand-600" />,
  pilotenprofil: <Award size={18} className="text-brand-600" />,
  drohnen: <Send size={18} className="text-brand-600" />,
  fluege: <MapPin size={18} className="text-brand-600" />,
  historie: <Wrench size={18} className="text-brand-600" />,
  "glossar-links": <BookOpen size={18} className="text-brand-600" />,
  einstellungen: <Sliders size={18} className="text-brand-600" />,
  abonnement: <CreditCard size={18} className="text-brand-600" />,
  dokumente: <FileText size={18} className="text-brand-600" />,
};

export default function MyDataPage() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useQuery<AccountSummary>({
    queryKey: ["/api/account/summary"],
    queryFn: async () => {
      const res = await fetch("/api/account/summary", { credentials: "include" });
      if (!res.ok) {
        let msg = `${res.status} ${res.statusText}`;
        try {
          const j = await res.json();
          if (j?.error) msg = String(j.error);
        } catch {
          /* noop */
        }
        throw new Error(msg);
      }
      return (await res.json()) as AccountSummary;
    },
  });

  async function handleExport() {
    if (exporting) return;
    setExporting(true);
    setError(null);
    try {
      const res = await fetch("/api/account/export", { credentials: "include" });
      if (!res.ok) {
        let msg = `${res.status} ${res.statusText}`;
        try {
          const j = await res.json();
          if (j?.error) msg = String(j.error);
        } catch {
          /* noop */
        }
        throw new Error(msg);
      }
      const text = await res.text();
      const stamp = new Date().toISOString().slice(0, 10);
      const fileName = `meine-daten-${stamp}.json`;
      const blob = new Blob([text], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export fehlgeschlagen");
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete() {
    if (deleting) return;
    const ok = window.confirm(
      "Konto unwiderruflich löschen? Alle serverseitig gespeicherten Daten und Dokumente werden dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.",
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch("/api/account", {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        let msg = `${res.status} ${res.statusText}`;
        try {
          const j = await res.json();
          if (j?.error) msg = String(j.error);
        } catch {
          /* noop */
        }
        throw new Error(msg);
      }
      await logout();
      qc.clear();
      window.alert("Dein Konto und alle zugehörigen Daten wurden gelöscht.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschung fehlgeschlagen");
      setDeleting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto pb-10 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-app-text)]">Meine Daten</h1>
        <p className="mt-1 text-sm text-[var(--color-app-text-secondary)] leading-relaxed">
          Hier siehst du, welche Daten wir über dich speichern. Du kannst deine
          Daten als maschinenlesbare JSON-Datei exportieren oder dein Konto
          unwiderruflich löschen.
        </p>
      </div>

      <div className="card flex items-center gap-3 px-4 py-3">
        <UserCheck size={18} className="text-[var(--color-go)] shrink-0" />
        <span className="text-sm text-[var(--color-app-text)]">
          Angemeldet als <strong>{user?.email}</strong>
        </span>
      </div>

      <DeletionCountdownNotice />

      {error ? (
        <div className="card flex items-start gap-3 px-4 py-3 border-2 border-[var(--color-nogo)] bg-[var(--color-nogo-bg)]">
          <Info size={18} className="text-[var(--color-nogo)] shrink-0 mt-0.5" />
          <span className="text-sm text-[var(--color-nogo)]">{error}</span>
        </div>
      ) : null}

      <section>
        <h2 className="text-[11px] font-bold tracking-wider text-[var(--color-app-text-secondary)] uppercase mb-2 px-1">
          Gespeicherte Daten
        </h2>
        {summaryLoading ? (
          <div className="card px-4 py-6 text-center text-sm text-[var(--color-app-text-secondary)]">
            Datenübersicht wird geladen …
          </div>
        ) : summaryError || !summary ? (
          <div className="card px-4 py-3 text-sm text-[var(--color-nogo)] border-2 border-[var(--color-nogo)] bg-[var(--color-nogo-bg)]">
            Die Datenübersicht konnte nicht geladen werden. Bitte versuche es
            später erneut.
          </div>
        ) : (
          <div className="card divide-y divide-[var(--color-app-border)]">
            {summary.groups.map((g) => (
              <div
                key={g.key}
                className="flex items-start gap-3 px-4 py-3"
                data-testid={`group-${g.key}`}
              >
                <span className="shrink-0 mt-0.5">
                  {GROUP_ICONS[g.key] ?? <Database size={18} className="text-brand-600" />}
                </span>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-[var(--color-app-text)]">
                      {g.title}
                    </div>
                    <span
                      className="shrink-0 inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full bg-brand-50 text-brand-600 text-xs font-bold"
                      data-testid={`count-${g.key}`}
                    >
                      {g.count}
                    </span>
                  </div>
                  {g.details.map((d, i) => (
                    <div
                      key={i}
                      className="text-xs text-[var(--color-app-text-secondary)] mt-0.5 leading-relaxed"
                    >
                      {d}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-[11px] font-bold tracking-wider text-[var(--color-app-text-secondary)] uppercase mb-2 px-1">
          Meine Rechte
        </h2>
        <div className="space-y-2">
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="card w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-app-bg-secondary)] transition text-left disabled:opacity-50"
            data-testid="button-export-data"
          >
            <Download size={18} className="text-brand-600 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-[var(--color-app-text)]">
                {exporting ? "Export wird erstellt …" : "Daten exportieren (JSON)"}
              </div>
              <div className="text-xs text-[var(--color-app-text-secondary)] mt-0.5 leading-relaxed">
                Lädt alle über dich gespeicherten Daten als maschinenlesbare
                Datei inkl. Dokument-Verweisen herunter.
              </div>
            </div>
            <ChevronRight size={18} className="text-[var(--color-app-text-tertiary)] shrink-0" />
          </button>

          <Link
            to="/datenschutz"
            className="card flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-app-bg-secondary)] transition"
            data-testid="link-privacy"
          >
            <Shield size={18} className="text-brand-600 shrink-0" />
            <span className="flex-1 text-sm font-semibold text-[var(--color-app-text)]">
              Datenschutzerklärung
            </span>
            <ChevronRight size={18} className="text-[var(--color-app-text-tertiary)] shrink-0" />
          </Link>

          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="card w-full flex items-center gap-3 px-4 py-3 border-2 border-[var(--color-nogo)] hover:bg-[var(--color-nogo-bg)] transition text-left disabled:opacity-50"
            data-testid="button-delete-account"
          >
            <Trash2 size={18} className="text-[var(--color-nogo)] shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-[var(--color-nogo)]">
                {deleting ? "Konto wird gelöscht …" : "Konto unwiderruflich löschen"}
              </div>
              <div className="text-xs text-[var(--color-app-text-secondary)] mt-0.5 leading-relaxed">
                Entfernt alle serverseitigen Daten und Dokumente dauerhaft und
                kündigt den Stripe-Kundenbezug.
              </div>
            </div>
          </button>
        </div>
      </section>
    </div>
  );
}
